
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../components/docs/Actions';
import Auth from 'plaid-threads/Icons/ProductAuth';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Errors and returns',
  description: 'Troubleshooting common errors and ACH returns'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`ACH returns and Transfer errors can both require investigation and troubleshooting.`}</p>
    <p>{`An ACH return means that your attempted ACH transfer was returned. This is typically not due to a Plaid issue and instead is caused by a problem with the ACH transfer itself, such as alleged fraud, insufficient funds, or a closed account. For more details, see `}<a parentName="p" {...{
        "href": "#ach-returns"
      }}>{`ACH Returns`}</a>{`.`}</p>
    <p>{`A Transfer error is an error from the Plaid Transfer API. Transfer errors are related to your interaction with the Plaid API. Causes may include bank API downtime, exceeding Plaid's limits for transfer activity, or attempting to use a Plaid Transfer feature you haven't been enabled for. For more details, see `}<a parentName="p" {...{
        "href": "#transfer-errors"
      }}>{`Transfer errors`}</a>{`.`}</p>
    <h4 {...{
      "id": "ach-returns"
    }}>{`ACH returns`}</h4>
    <p>{`After an ACH payment has been posted, it can be `}<a parentName="p" {...{
        "href": "https://plaid.com/resources/ach/ach-return/"
      }}>{`returned`}</a>{` and clawed back from your account for a number of reasons. For example, a bank could determine that there are insufficient funds to complete the transfer, or a consumer can contact their bank and flag the transaction as unauthorized. If a debit transfer results in an ACH return, the funds will be automatically clawed back in a return sweep. If a credit transfer experiences an ACH return, the funds will be returned to your Ledger available balance.`}</p>
    <p>{`Most ACH returns (including all R01 "insufficient funds" returns, which are by far the most common return type) must occur within 2 banking days of the transaction entering the `}<inlineCode parentName="p">{`settled`}</inlineCode>{` status. For example, if a transaction was settled on Monday, then after close of business on Wednesday, it would be safe from an R01 return. However, for ACH returns in which the consumer alleges that the transaction was unauthorized, including return codes such as R10 or R11, the window is 60 calendar days. `}</p>
    <p>{`Note that an ACH return is distinct from an ACH reversal. An ACH reversal is performed only to correct an erroneous transfer and will only be done at the request of the originator (i.e. you). For details, see `}<a parentName="p" {...{
        "href": "#escalating-issues-with-ach-transfers"
      }}>{`Escalating issues with ACH transfers`}</a>{`.`}</p>
    <p>{`If an ACH transfer is returned, its status will be `}<inlineCode parentName="p">{`returned`}</inlineCode>{`. All returned ACH transactions will have an ACH return code. By reading the code, you can troubleshoot returned transactions. `}</p>
    <p>{`For more detailed troubleshooting information, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881797799575-What-are-the-Common-ACH-Return-Codes"
      }}>{`Common ACH Return Codes in the Help Center`}</a>{`.`}</p>
    <p>{`For a full list of ACH return codes, see `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#ach-return-codes"
      }}>{`ACH Return Codes`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Return Code`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Notes`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Troubleshooting`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`R01`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Insufficient funds`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Available balance is not sufficient to cover the dollar amount of the debit entry.`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`If you are submitting the transfer on a Friday, we recommend using Same-Day ACH to decrease the likelihood of the user's account balance dipping below the transfer amount over the weekend.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`R03`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`No account or unable to locate account`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The account number structure is valid, but the account number does not correspond to an existing account, or the name on the transaction does not match the name on the account.`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Prompt the user to link another account via Plaid Link and use this newly linked account to create the transfer.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`R09`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Uncollected funds`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Available balance is sufficient, but the collected balance is not sufficient to cover the entry`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Retry the transaction a few days later after any large "holds" on the user's account have been released.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`R10`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Customer advises not authorized`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The receiver advises that the originator is not known and/or is not authorized to debit the receiver's account.`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Make sure you add proper WEB debit authorization language to your "pay with bank" UX to protect against exposure to returns of over 60 days. Unlike other ACH return codes, you cannot resubmit a payment after encountering an R10 error.`}</td>
        </tr>
      </tbody>
    </table>
    <Callout warning mdxType="Callout">
      <p>{`A returned transfer can be retried up to 2 times. When you try to reprocess a returned transfer, the `}<inlineCode parentName="p">{`description`}</inlineCode>{` field in your `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{` request must be `}<inlineCode parentName="p">{`"Retry 1"`}</inlineCode>{` or `}<inlineCode parentName="p">{`"Retry 2"`}</inlineCode>{` to indicate that it's a retry of a previously returned transfer. Note that retries are only allowed for transfers returned as R01 or R09.`}</p>
    </Callout>
    <h5 {...{
      "id": "ach-return-categories"
    }}>{`ACH return categories`}</h5>
    <p>{`ACH return rates are broken into three categories. `}</p>
    <p><em parentName="p">{`Administrative`}</em>{`: The transfer could not be processed due to a technical error with the account. Common return codes in this category include R02 (account closed), R03 (no account), and R04 (invalid account number). `}</p>
    <p><em parentName="p">{`Unauthorized`}</em>{`: The transfer was reported as unauthorized. Common return codes in this category include R10 (customer advises originator is not known and/or not authorized to debit account), R11 (customer advises entry not in accordance with the terms of the authorization, e.g., the amount or date differs from what was authorized), and R07 (customer advises authorization to debit account was revoked).`}</p>
    <p><em parentName="p">{`Other`}</em>{`: All other returns. The most common code in this category is R01 (insufficient funds).`}</p>
    <h6 {...{
      "id": "return-rate-limits"
    }}>{`Return rate limits`}</h6>
    <p>{`You can view your current return rates by category on the Transfer Dashboard. Plaid's maximum limits for ACH debit return rates are:`}</p>
    <ul>
      <li parentName="ul">{`Overall: 12`}{`%`}</li>
      <li parentName="ul">{`Administrative: 2.5`}{`%`}</li>
      <li parentName="ul">{`Unauthorized: 0.4`}{`%`}</li>
    </ul>
    <p>{`For more details, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881513531671-What-are-my-obligations-as-an-ACH-Originator#h_01JYHCEY1RT625QMSVWQPDEVPB"
      }}>{`ACH Debit Return Rate Thresholds`}</a>{`.`}</p>
    <p>{`If you exceed these limits in a 60 day period, you will be contacted by Plaid with a warning email. Failure to reduce return rates below these limits within three months may result in a fine. If excessive return rates remain unaddressed for an extended period of time, your access to Plaid Transfer may be revoked. If you are having trouble reducing your return rates below these limits, contact your Plaid account manager for help.  `}</p>
    <h4 {...{
      "id": "rtp-failures"
    }}>{`RTP failures`}</h4>
    <p>{`Plaid performs various checks within `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` to fail upfront and as early as possible if the RTP or FedNow transfer won't succeed. However, due to the asynchronous nature of the RTP and FedNow payment networks, it is still possible for transfers to fail after authorization due to a rejection by the receiving bank or an issue in the payment network. Failures in the real-time networks are rare (<1`}{`%`}{`), and often indicate that the consumer bank account itself is in an unusable state.`}</p>
    <p>{`If the RTP transfer failed, the status will be `}<inlineCode parentName="p">{`failed`}</inlineCode>{` and an error code will be present in the transfer's `}<inlineCode parentName="p">{`failure_reason.failure_code`}</inlineCode>{` property. For the following failure codes, further attempts to use this bank account for payouts via the real-time network are unlikely to succeed. Instead, we recommend that you prompt your user to link a different bank account via Plaid Link.`}</p>
    <p>{`For a full list of RTP failure codes, see `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#rtprfp-error-codes"
      }}>{`RTP / RfP error codes`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Failure Code`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Notes`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`AC03`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Creditor account is invalid`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Account number is not recognized as valid by the receiving bank.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`AC04`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Account closed`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The account is closed and no longer active.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`AC06`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Account is blocked`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Receiving bank has placed a block on the account, for various operational or legal reasons.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`E997`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Timeout clock has expired`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The timeout for processing this transaction was exceeded in the FedNow network. You can safely retry the payment with a different idempotency key.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "transfer-errors"
    }}>{`Transfer errors`}</h4>
    <p>{`For error codes specific to transfer and accompanying troubleshooting steps, including for common errors `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#transfer_network_limit_exceeded"
      }}><inlineCode parentName="a">{`TRANSFER_NETWORK_LIMIT_EXCEEDED`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#transfer_account_blocked"
      }}><inlineCode parentName="a">{`TRANSFER_ACCOUNT_BLOCKED`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#transfer_forbidden_ach_class"
      }}><inlineCode parentName="a">{`TRANSFER_FORBIDDEN_ACH_CLASS`}</inlineCode></a>{` see `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/"
      }}>{`Transfer error codes`}</a>{`.`}</p>
    <h5 {...{
      "id": "handling-500-errors-in-transfer-creation"
    }}>{`Handling 500 errors in transfer creation`}</h5>
    <p>{`You may occasionally encounter a 500 HTTP error when creating a transfer via `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` or `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{`. In this case, it's possible that the authorization or transfer was created successfully, but the response was not received. It is highly recommended to use the `}<inlineCode parentName="p">{`idempotency_key`}</inlineCode>{` field in `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{` so that retries in this scenario are safe to perform and will not result in duplicate authorizations. Retrying `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{` with the same `}<inlineCode parentName="p">{`authorization_id`}</inlineCode>{` is guaranteed to only ever produce a single transfer.`}</p>
    <p>{`Whether you retry the request to `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{` or not, it's possible that the first request succeeded, and so your integration should be prepared to consume events from `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{` that you may not have seen the `}<inlineCode parentName="p">{`transfer_id`}</inlineCode>{` for previously. It is recommended you have a recovery process in place, either by logging these instances for manual review and reconciliation, or automatically reconciling by pulling the transfer details via `}<inlineCode parentName="p">{`/transfer/get`}</inlineCode>{` and using the `}<inlineCode parentName="p">{`transfer.metadata`}</inlineCode>{` property to utilize any client-side data you attached to the transfer for reference. `}</p>
    <p>{`Another way to determine if a transfer creation succeeded after receiving a 500 error from `}<inlineCode parentName="p">{`/transfer/create`}</inlineCode>{` is to follow up with a call to `}<inlineCode parentName="p">{`/transfer/get`}</inlineCode>{` using the `}<inlineCode parentName="p">{`authorization_id`}</inlineCode>{` you received from `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{`. If the transfer was actually created, you will receive a successful response containing the transfer details. If it wasn't, you will receive a 404 HTTP error with the `}<inlineCode parentName="p">{`NOT_FOUND`}</inlineCode>{` error code.`}</p>
    <h4 {...{
      "id": "escalating-issues-with-ach-transfers"
    }}>{`Escalating issues with ACH transfers`}</h4>
    <p>{`In the event of a problem with an ACH transfer, you can escalate to Plaid. Typical reasons to request an escalation include:`}</p>
    <ul>
      <li parentName="ul">{`You accidentally submitted an erroneous transfer`}</li>
      <li parentName="ul">{`Your end-customer claims that they did not receive funds you sent them`}</li>
      <li parentName="ul">{`Your end-customer claims that you made an unauthorized debit from their account`}</li>
    </ul>
    <p>{`Available escalation types include reversals, recalls, trace requests, and written statement of unauthorized debit (WSUD) requests.`}</p>
    <p>{`To learn more about escalations, including which escalation type is appropriate for your situation and how to submit an escalation, see `}<a parentName="p" {...{
        "href": "https://support.plaid.com/hc/en-us/articles/32881719199767-How-are-ACH-Transfer-Investigations-Escalated"
      }}>{`How are ACH Transfer Investigations Escalated?`}</a></p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "ach-returns",
        level: 4,
        title: "ACH returns",
      },
{
        id: "rtp-failures",
        level: 4,
        title: "RTP failures",
      },
{
        id: "transfer-errors",
        level: 4,
        title: "Transfer errors",
      },
{
        id: "escalating-issues-with-ach-transfers",
        level: 4,
        title: "Escalating issues with ACH transfers",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
